import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import LogoMark from "@/components/LogoMark";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import AdminNews from "@/components/admin/AdminNews";
import AdminBlast from "@/components/admin/AdminBlast";
import AdminFixtures from "@/components/admin/AdminFixtures";
import StatusControl from "@/components/admin/StatusControl";
import DraftReplyButton from "@/components/admin/DraftReplyButton";

function StatCard({ label, value, tone }) {
  return (
    <div className="rounded-[14px] p-6 text-white" style={{ background: tone }} data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-xs uppercase tracking-[0.14em] font-bold text-white/80">{label}</div>
      <div className="font-display text-4xl mt-2">{value}</div>
    </div>
  );
}

function DataTable({ rows, columns, empty, testid }) {
  if (!rows) return <div className="p-8 text-ink/60">Loading…</div>;
  if (rows.length === 0) return <div className="p-8 text-ink/60">{empty}</div>;
  return (
    <div className="overflow-auto border border-ink/10 rounded-[14px] bg-white" data-testid={testid}>
      <table className="min-w-full text-sm">
        <thead className="bg-ink/5">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left font-semibold p-3 border-b border-ink/10">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-ink/5 hover:bg-mango/10">
              {columns.map((c) => (
                <td key={c.key} className="p-3 align-top">{c.render ? c.render(r) : String(r[c.key] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ page, pages, total, onPage, testid }) {
  if (!pages || pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4" data-testid={`${testid}-pagination`}>
      <span className="text-sm text-ink/60">
        Page {page} of {pages} · {total} total
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid={`${testid}-prev-page`}
          className="inline-flex items-center gap-1 border-2 border-ink/15 rounded-full px-4 py-1.5 text-sm font-bold hover:border-palm hover:text-palm transition disabled:opacity-40 disabled:pointer-events-none"
        >
          <ChevronLeft size={15} /> Prev
        </button>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          data-testid={`${testid}-next-page`}
          className="inline-flex items-center gap-1 border-2 border-ink/15 rounded-full px-4 py-1.5 text-sm font-bold hover:border-palm hover:text-palm transition disabled:opacity-40 disabled:pointer-events-none"
        >
          Next <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}

/**
 * Paginated admin table. `columns` is a function receiving updateRow (merge a changed row back in).
 */
function PagedTable({ endpoint, columns, empty, testid, limit = 10 }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(
    (p) => {
      api
        .get(endpoint, { params: { page: p, limit } })
        .then((r) => setData(r.data))
        .catch(() => setData({ items: [], total: 0, page: 1, pages: 1 }));
    },
    [endpoint, limit]
  );

  useEffect(() => {
    load(page);
  }, [page, load]);

  const updateRow = (u) =>
    setData((d) => (d ? { ...d, items: d.items.map((x) => (x.id === u.id ? { ...x, ...u } : x)) } : d));

  return (
    <>
      <DataTable rows={data ? data.items : null} columns={columns(updateRow)} empty={empty} testid={testid} />
      {data && <Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid={testid} />}
    </>
  );
}

const fmtDate = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user]);

  if (checking) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;

  async function handleLogout() {
    await logout();
    nav("/admin");
  }

  return (
    <section className="py-14 min-h-screen">
      <div className="max-w-[1240px] mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <LogoMark size={40} />
            <div>
              <div className="font-display text-xl leading-none">HOFA Admin</div>
              <div className="text-xs uppercase tracking-[0.14em] text-palm font-bold mt-1">Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-ink/70">{user.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border-2 border-ink/15 rounded-full px-4 py-2 text-sm font-bold hover:border-berry hover:text-berry transition">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
            <StatCard label="Contacts" value={stats.contacts} tone="#1F5C38" />
            <StatCard label="Volunteers" value={stats.volunteers} tone="#D98A18" />
            <StatCard label="Partners" value={stats.partners} tone="#C05A3A" />
            <StatCard label="Newsletter" value={stats.newsletter} tone="#31517E" />
            <StatCard label="Donations" value={stats.donations_paid} tone="#A62639" />
            <StatCard label="Pledges" value={stats.pledges || 0} tone="#8B2C3F" />
            <StatCard label="Raised (USD)" value={`$${(stats.donations_amount_usd || 0).toFixed(0)}`} tone="#123B23" />
          </div>
        )}

        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="bg-white border border-ink/10 rounded-full p-1 mb-6 flex-wrap">
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Contacts</TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="admin-tab-volunteers" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Volunteers</TabsTrigger>
            <TabsTrigger value="partners" data-testid="admin-tab-partners" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Partners</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Newsletter</TabsTrigger>
            <TabsTrigger value="donations" data-testid="admin-tab-donations" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Donations</TabsTrigger>
            <TabsTrigger value="pledges" data-testid="admin-tab-pledges" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Pledges</TabsTrigger>
            <TabsTrigger value="fixtures" data-testid="admin-tab-fixtures" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">Fixtures</TabsTrigger>
            <TabsTrigger value="news" data-testid="admin-tab-news" className="rounded-full data-[state=active]:bg-palm data-[state=active]:text-white px-5">News</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            <PagedTable
              endpoint="/admin/contacts"
              testid="admin-contacts-table"
              empty="No contact submissions yet."
              columns={(updateRow) => [
                { key: "status", label: "Status", render: (r) => <div className="flex flex-col gap-1.5 items-start"><StatusControl kind="contacts" row={r} onChange={updateRow} /><DraftReplyButton kind="contact" submission={r} onSaved={updateRow} /></div> },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "subject", label: "Subject" },
                { key: "message", label: "Message", render: (r) => <div className="max-w-[380px] whitespace-pre-wrap">{r.message}</div> },
                { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) },
              ]}
            />
          </TabsContent>

          <TabsContent value="volunteers">
            <PagedTable
              endpoint="/admin/volunteers"
              testid="admin-volunteers-table"
              empty="No volunteer submissions yet."
              columns={(updateRow) => [
                { key: "status", label: "Status", render: (r) => <div className="flex flex-col gap-1.5 items-start"><StatusControl kind="volunteers" row={r} onChange={updateRow} /><DraftReplyButton kind="volunteer" submission={r} onSaved={updateRow} /></div> },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "location", label: "Location" },
                { key: "skills", label: "Skills" },
                { key: "availability", label: "Availability" },
                { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) },
              ]}
            />
          </TabsContent>

          <TabsContent value="partners">
            <PagedTable
              endpoint="/admin/partners"
              testid="admin-partners-table"
              empty="No partner submissions yet."
              columns={(updateRow) => [
                { key: "status", label: "Status", render: (r) => <div className="flex flex-col gap-1.5 items-start"><StatusControl kind="partners" row={r} onChange={updateRow} /><DraftReplyButton kind="partner" submission={r} onSaved={updateRow} /></div> },
                { key: "organization", label: "Organization" },
                { key: "contact_name", label: "Contact" },
                { key: "email", label: "Email" },
                { key: "partnership_type", label: "Type" },
                { key: "message", label: "Message", render: (r) => <div className="max-w-[340px] whitespace-pre-wrap">{r.message}</div> },
                { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) },
              ]}
            />
          </TabsContent>

          <TabsContent value="newsletter">
            <AdminBlast subscriberCount={stats?.newsletter} />
            <PagedTable
              endpoint="/admin/newsletter"
              testid="admin-newsletter-table"
              empty="No newsletter subscribers yet."
              columns={() => [
                { key: "email", label: "Email" },
                { key: "name", label: "Name" },
                { key: "created_at", label: "Subscribed", render: (r) => fmtDate(r.created_at) },
              ]}
            />
          </TabsContent>

          <TabsContent value="donations">
            <PagedTable
              endpoint="/admin/donations"
              testid="admin-donations-table"
              empty="No donation records yet."
              columns={() => [
                { key: "donor_name", label: "Donor" },
                { key: "donor_email", label: "Email" },
                { key: "amount", label: "Amount", render: (r) => `$${Number(r.amount || 0).toFixed(2)}` },
                { key: "package_id", label: "Package" },
                { key: "payment_status", label: "Status", render: (r) => (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.payment_status === "paid" ? "bg-palm text-white" : r.payment_status === "unpaid" || r.payment_status === "pending" ? "bg-mango text-palm-deep" : "bg-berry text-white"}`}>
                    {r.payment_status}
                  </span>
                )},
                { key: "receipt_sent_at", label: "Receipt", render: (r) => (r.receipt_sent_at ? <span className="text-palm text-xs font-bold">Emailed</span> : <span className="text-ink/40 text-xs">—</span>) },
                { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) },
              ]}
            />
          </TabsContent>

          <TabsContent value="pledges">
            <PagedTable
              endpoint="/admin/pledges"
              testid="admin-pledges-table"
              empty="No monthly pledges yet."
              columns={(updateRow) => [
                { key: "status", label: "Status", render: (r) => <div className="flex flex-col gap-1.5 items-start"><StatusControl kind="pledges" row={r} onChange={updateRow} /><DraftReplyButton kind="pledge" submission={r} onSaved={updateRow} /></div> },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "monthly_amount", label: "Monthly", render: (r) => `$${Number(r.monthly_amount || 0).toFixed(2)}/mo` },
                { key: "start_month", label: "Start" },
                { key: "note", label: "Note", render: (r) => <div className="max-w-[300px] whitespace-pre-wrap">{r.note}</div> },
                { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) },
              ]}
            />
          </TabsContent>

          <TabsContent value="fixtures">
            <AdminFixtures />
          </TabsContent>

          <TabsContent value="news">
            <AdminNews />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
